import { resolvePublicFlags } from '@apruv/survey-contract/flags';
import type { CompiledSurvey } from '@apruv/survey-contract/compiler';
import type { CXPublicSurveyResponse } from '../../services/cxPublicSurveyService';

const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Compare flag records and scalars independently of API object-key ordering. */
export function sameFlagSnapshot(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (!object(left) || !object(right)) return false;
  const keys = Object.keys(left);
  return (
    keys.length === Object.keys(right).length &&
    keys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(right, key) && sameFlagSnapshot(left[key], right[key])
    )
  );
}

/** Only the pinned API snapshot supplies inputs. Raw Event payload and answers never do. */
export function canonicalSurveyFlags(compiled: CompiledSurvey, response: CXPublicSurveyResponse) {
  const snapshot = response.runtimeContext?.flagSnapshot;
  if (snapshot === undefined) {
    if (compiled.snapshot.flags.bindings.length || compiled.snapshot.flags.guards.length)
      throw new Error('This survey requires an authoritative flag snapshot before it can run.');
    return Object.freeze({});
  }
  const keys = [
    'schemaVersion',
    'resolverVersion',
    'identity',
    'eventFlowVersionId',
    'runId',
    'stepId',
    'requestedFlags',
    'resolvedFlags',
    'provenance',
  ];
  if (
    !object(snapshot) ||
    Object.keys(snapshot).length !== keys.length ||
    keys.some((key) => !Object.prototype.hasOwnProperty.call(snapshot, key)) ||
    snapshot.schemaVersion !== 1 ||
    snapshot.resolverVersion !== 'survey-flags/v1' ||
    !object(snapshot.identity) ||
    !object(snapshot.requestedFlags) ||
    !object(snapshot.resolvedFlags) ||
    !object(snapshot.provenance) ||
    typeof snapshot.eventFlowVersionId !== 'string' ||
    !snapshot.eventFlowVersionId.trim() ||
    typeof snapshot.runId !== 'string' ||
    !snapshot.runId.trim() ||
    typeof snapshot.stepId !== 'string' ||
    !snapshot.stepId.trim() ||
    snapshot.runId !== response.triggerGroupId ||
    snapshot.stepId !== response.executionStepId ||
    compiled.snapshot.identity.organizationId !== response.runtimeContext?.organizationId ||
    compiled.snapshot.identity.experienceVersionId !== response.moduleRevisionId
  )
    throw new Error('Invalid survey flag snapshot identity or version.');
  const resolution = resolvePublicFlags(
    compiled.snapshot,
    compiled.snapshot.identity,
    snapshot.requestedFlags
  );
  if (
    !sameFlagSnapshot(snapshot.identity, resolution.identity) ||
    !sameFlagSnapshot(snapshot.resolvedFlags, resolution.resolvedFlags) ||
    !sameFlagSnapshot(snapshot.provenance, resolution.provenance)
  )
    throw new Error('The survey flag snapshot does not match its pinned document.');
  return resolution.resolvedFlags;
}
